import React, { useEffect, useRef, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Col, Grid, Input, Line, Span } from 'components'
import {
  DraggableDialog,
  resetModalState
} from 'components/DraggableDialog/DraggableDialog'
import { SuccessModal } from 'components/SuccessModal/SuccessModal'
import useLoading from 'hooks/useLoading'
import Draggable from 'react-draggable'
import { useForm } from 'react-hook-form'
import { ISindicato } from 'services/NGT/Syndicate'
import { buscaCep } from 'services/Utils/buscaCep'
import { SPACING } from 'utils'
import * as yup from 'yup'

import {
  DialogActionsStyled,
  DialogContentStyled,
  DialogTitleStyled,
  FormDialog
} from './EditSyndicateModal.style'

interface IEditSyndicateModal {
  showEditModal: boolean
  toggleModal: () => void
  handleSubmitUpdate: (data: any) => Promise<void>
  syndicateDataTable: ISindicato
  handleClearFilters: () => void
}

const schema = yup
  .object({
    cdCep: yup.string().min(9, 'CEP inválido').required('Informe o CEP'),
    cdCnpj: yup
      .string()
      .required('Informe o CNPJ')
      .transform((value) => value.replace(/[^\d]/g, ''))
      .min(14, 'CNPJ inválido'),
    nmBairro: yup
      .string()
      .required('Informe o bairro.')
      .min(2, 'Bairro inválido.')
      .max(50, 'Máximo 50 caracteres'),
    nmSindicato: yup
      .string()
      .required('Informe o Nome do Sindicato')
      .min(10, 'Mínimo 10 caracteres')
      .max(100, 'Máximo 100 caracteres'),
    nuNumero: yup.string().required('Informe o número'),
    sgEstado: yup
      .string()
      .min(2, 'Estado inválido.')
      .max(2, 'Máximo 2 lestras')
      .required('Informe o estado.'),
    sgPais: yup
      .string()
      .min(2, 'País inválido')
      .max(2, 'Máximo 2 lestras')
      .required('Informe o país'),
    txCidade: yup
      .string()
      .min(3, 'Cidade inválida.')
      .max(50, 'Máximo 50 caracteres')
      .required('Informe a cidade.'),
    txComplemento: yup.string().nullable().max(50, 'Máximo 50 caracteres'),
    txEndereco: yup
      .string()
      .min(5, 'Mínimo 5 caracteres')
      .required('Informe o Endereço')
    //txObservacao: yup.string().max(100, 'Máximo 100 caracteres')
    //txTipo: yup.string().required("Informe o Tipo"),
  })
  .required()

const EditSyndicateModal: React.FC<IEditSyndicateModal> = ({
  showEditModal,
  toggleModal,
  handleSubmitUpdate,
  syndicateDataTable,
  handleClearFilters
}) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm<ISindicato>({ resolver: yupResolver(schema) })

  const form = watch()
  const [syndicateData, setSyndicateData] = useState<ISindicato>()

  useEffect(() => {
    setSyndicateData(syndicateDataTable)
    setValue('cdCnpj', syndicateDataTable?.cdCnpj)
    setValue('nmSindicato', syndicateDataTable?.nmSindicato)
    setValue('cdCep', syndicateDataTable?.cdCep)
    setValue('txEndereco', syndicateDataTable?.txEndereco)
    setValue('nuNumero', syndicateDataTable?.nuNumero)
    setValue('txComplemento', syndicateDataTable?.txComplemento)
    setValue('nmBairro', syndicateDataTable?.nmBairro)
    setValue('txCidade', syndicateDataTable?.txCidade)
    setValue('sgEstado', syndicateDataTable?.sgEstado)
    setValue('sgPais', syndicateDataTable?.sgPais)
    setValue('txObservacao', syndicateDataTable?.txObservacao)
  }, [syndicateDataTable])

  const resetForm = () => {
    setSyndicateData(syndicateDataTable)
    setValue('cdCnpj', syndicateDataTable?.cdCnpj)
    setValue('nmSindicato', syndicateDataTable?.nmSindicato)
    setValue('cdCep', syndicateDataTable?.cdCep)
    setValue('txEndereco', syndicateDataTable?.txEndereco)
    setValue('nuNumero', syndicateDataTable?.nuNumero)
    setValue('txComplemento', syndicateDataTable?.txComplemento)
    setValue('nmBairro', syndicateDataTable?.nmBairro)
    setValue('txCidade', syndicateDataTable?.txCidade)
    setValue('sgEstado', syndicateDataTable?.sgEstado)
    setValue('sgPais', syndicateDataTable?.sgPais)
    setValue('txObservacao', syndicateDataTable?.txObservacao)
  }

  useEffect(() => {
    if (form.cdCep?.length > 7) {
      buscaCep(form.cdCep).then(({ data }) => {
        reset(
          {
            nmBairro: '',
            sgEstado: '',
            txCidade: '',
            txEndereco: ''
          },
          {
            keepValues: true
          }
        )

        setValue(
          'txEndereco',
          data?.logradouro !== '' ? data?.logradouro : form.txEndereco
        )
        setValue('nmBairro', data?.bairro !== '' ? data?.bairro : form.nmBairro)
        setValue('txCidade', data?.localidade)
        setValue('sgEstado', data?.uf)
      })
    }
  }, [form.cdCep])

  const { loading, msgErro, setLoading } = useLoading()

  const draggableReference = useRef<Draggable>(null)

  return (
    <DraggableDialog
      reference={draggableReference}
      showModal={showEditModal}
      onClose={toggleModal}
      width={'xl'}
    >
      <FormDialog
        onSubmit={handleSubmit((data) => {
          handleSubmitUpdate({
            ...data,
            idSindicato: syndicateData?.idSindicato
          })
          reset()
          resetModalState(draggableReference)
          resetForm()
        })}
      >
        <Line />
        <DialogTitleStyled>Editar Sindicato</DialogTitleStyled>
        <DialogContentStyled>
          <Grid
            gridCols="1fr 7fr"
            gridGap={`0 ${SPACING.spacing10}`}
            margin={`${SPACING.spacing30} 0 ${SPACING.spacing30} 0`}
          >
            <Input
              control={control}
              label="CNPJ Sindicato"
              formValue="cdCnpj"
              defaultValue={syndicateData?.cdCnpj}
              value={syndicateData?.cdCnpj}
              placeholder="CNPJ"
              errors={errors}
              mask={['##.###.###/####-##']}
              required
            />
            <Input
              control={control}
              label="Sindicato/Confederação"
              formValue="nmSindicato"
              defaultValue={syndicateData?.nmSindicato}
              placeholder="Nome"
              errors={errors}
              required
              maxLength={100}
            />
          </Grid>
          <Grid
            gridCols="1fr 7fr"
            gridGap={`0 ${SPACING.spacing10}`}
            margin={`${SPACING.spacing30} 0 ${SPACING.spacing30} 0`}
          >
            <Input
              control={control}
              label="CEP"
              formValue="cdCep"
              placeholder="00000-000"
              defaultValue={syndicateData?.cdCep}
              mask={['#####-###']}
              errors={errors}
              required
            />
            <Input
              control={control}
              label="Endereço"
              formValue="txEndereco"
              defaultValue={syndicateData?.txEndereco}
              placeholder="Endereço"
              errors={errors}
              required
              maxLength={50}
            />
          </Grid>
          <Grid
            gridCols="1fr 1fr 2fr 2fr 1fr 1fr"
            gridGap={`0 ${SPACING.spacing10}`}
            margin={`0 0 ${SPACING.spacing30} 0`}
          >
            <Input
              control={control}
              label="Nº do Logradouro"
              formValue="nuNumero"
              defaultValue={syndicateData?.nuNumero}
              placeholder="Nº"
              errors={errors}
              required
              maxLength={10}
            />
            <Input
              control={control}
              label="Complemento"
              formValue="txComplemento"
              defaultValue={syndicateData?.txComplemento}
              placeholder="Complemento"
              maxLength={50}
            />
            <Input
              control={control}
              label="Bairro"
              formValue="nmBairro"
              defaultValue={syndicateData?.nmBairro}
              placeholder="Bairro"
              errors={errors}
              required
              maxLength={50}
            />
            <Input
              control={control}
              label="Cidade"
              formValue="txCidade"
              defaultValue={syndicateData?.txCidade}
              placeholder="Cidade"
              errors={errors}
              required
              maxLength={50}
            />
            <Input
              control={control}
              label="Estado"
              formValue="sgEstado"
              defaultValue={syndicateData?.sgEstado}
              placeholder="UF"
              errors={errors}
              maxLength={2}
              required
            />
            <Input
              control={control}
              label="País"
              formValue="sgPais"
              defaultValue={syndicateData?.sgPais}
              placeholder="Pais"
              errors={errors}
              maxLength={2}
              required
            />
          </Grid>

          <Grid
            gridCols="4fr 4fr"
            gridGap={`0 ${SPACING.spacing10}`}
            margin={`0 0 ${SPACING.spacing30} 0`}
          >
            <Col>
              <Input
                control={control}
                label="Observação"
                formValue="txObservacao"
                defaultValue={syndicateData?.txObservacao}
                placeholder="Observação"
                maxLength={50}
              />
              <Span
                textAlign="end"
                text={String(form.txObservacao?.length || '0') + '/50'}
              />
            </Col>
          </Grid>

          <SuccessModal
            open={loading === 'error'}
            message={msgErro}
            type="error"
            hideButtonCancel
            handleConfirm={() => setLoading('idle')}
            txtButtonConfirm="Voltar"
          />
        </DialogContentStyled>

        <DialogActionsStyled>
          <Button
            text="Voltar"
            variantButton="text"
            marginRight={SPACING.spacing20}
            marginBottom={SPACING.spacing10}
            onClick={() => {
              toggleModal()
              resetModalState(draggableReference)
            }}
          />
          <Button
            text="SALVAR"
            type="submit"
            onClick={() => {
              handleClearFilters()
              resetModalState(draggableReference)
            }}
            marginBottom={SPACING.spacing10}
          />
        </DialogActionsStyled>
      </FormDialog>
    </DraggableDialog>
  )
}

export default EditSyndicateModal
